/**
 * BookContent — progressive-disclosure Table of Contents for a Volume.
 * Sections collapse; expanding reveals subsections + direct pages. Reader
 * navigation is strictly by stable IDs + sort_order (never by title).
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getVolumeEntry, searchBook } from "@/lib/bookApi";

function IconChevron({ open }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export default function BookContent() {
  const { slug = "almond" } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [openSecId, setOpenSecId] = useState(null);
  const [openSubId, setOpenSubId] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getVolumeEntry(slug);
        if (!d.has_access && !d.is_editor) {
          setErr("Доступ к Тому не оформлен.");
          return;
        }
        setData(d);
        // Auto-open the first section (progressive disclosure UX).
        if (d.sections?.length) setOpenSecId(d.sections[0].id);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (typeof detail === "object" && detail?.code === "no_access") {
          setErr("Доступ к Тому не оформлен.");
        } else if (e?.response?.status === 404) {
          setErr("Том не найден.");
        } else {
          setErr("Не удалось загрузить Том.");
        }
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!q.trim()) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchBook(slug, q.trim());
        setSearchResults(res);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, slug]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#efe6d5] text-[#3b241a] px-6">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3">{err}</div>
          <button
            className="mt-5 px-6 py-3 rounded-full bg-[#2a1810] text-[#efe4d0] tracking-[0.24em] text-xs uppercase"
            onClick={() => nav(`/book/${slug}/entry`)}
            data-testid="book-content-back-entry">
            К обложке
          </button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-[#efe6d5]" data-testid="book-content-loading" />;

  const openPage = (pageId) => nav(`/book/${slug}/pages/${pageId}`);

  return (
    <div className="min-h-screen bg-[#efe6d5] text-[#3b241a]" data-testid="book-content-screen">
      {/* Header */}
      <div className="max-w-[860px] mx-auto px-6 lg:px-10 pt-10 lg:pt-16 pb-6">
        <button
          onClick={() => nav(`/book/${slug}/entry`)}
          className="text-[11px] tracking-[0.42em] uppercase opacity-70 hover:opacity-100"
          data-testid="book-content-back">
          ← К обложке
        </button>
        <h1
          className="font-serif mt-6 leading-[1.02]"
          style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
                   fontSize: "clamp(38px, 5vw, 68px)", color: "#2a1810" }}
          data-testid="book-content-volume-title">
          {data.volume.title}
        </h1>
        {data.volume.subtitle && (
          <div className="italic text-[17px] mt-2 opacity-80">
            {data.volume.subtitle}
          </div>
        )}
        <div className="mt-10">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по Тому"
            className="w-full bg-transparent border-b border-[#3b241a]/30 focus:border-[#3b241a] outline-none py-2.5 text-[15px] placeholder:text-[#3b241a]/40"
            data-testid="book-content-search"
          />
        </div>
      </div>

      {/* Search results */}
      {searchResults !== null && (
        <div className="max-w-[860px] mx-auto px-6 lg:px-10 pb-16">
          {searching && <div className="opacity-60 text-sm">Поиск…</div>}
          {!searching && searchResults.length === 0 && (
            <div className="opacity-60 text-sm">Ничего не найдено.</div>
          )}
          {!searching && searchResults.map((r) => (
            <button
              key={r.id}
              onClick={() => openPage(r.id)}
              className="w-full text-left py-4 border-b border-[#3b241a]/12 hover:bg-[#3b241a]/5"
              data-testid={`book-content-search-result-${r.id}`}>
              <div className="font-serif text-[19px]">{r.title}</div>
              {r.subtitle && <div className="text-xs opacity-70 mt-1">{r.subtitle}</div>}
            </button>
          ))}
        </div>
      )}

      {/* TOC */}
      {searchResults === null && (
        <div className="max-w-[860px] mx-auto px-6 lg:px-10 pb-24">
          {data.sections.length === 0 && (
            <div className="mt-16 py-16 text-center opacity-70">
              <div className="font-serif text-2xl mb-2">Оглавление скоро появится</div>
              <div className="text-sm">Автор готовит первые главы Тома.</div>
            </div>
          )}
          <ol className="mt-2">
            {data.sections.map((sec, secIdx) => {
              const open = openSecId === sec.id;
              const total = sec.pages.length + sec.subsections.reduce((n, s) => n + s.pages.length, 0);
              return (
                <li key={sec.id} className="border-b border-[#3b241a]/15">
                  <button
                    onClick={() => setOpenSecId(open ? null : sec.id)}
                    className="w-full flex items-center gap-4 py-6 text-left"
                    data-testid={`book-content-section-${sec.id}`}>
                    <span className="w-8 text-[11px] tracking-[0.3em] opacity-60">
                      {String(secIdx + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1">
                      <span className="font-serif text-[24px] block leading-tight">
                        {sec.title}
                      </span>
                      {sec.subtitle && (
                        <span className="text-[13px] opacity-70 block mt-1">{sec.subtitle}</span>
                      )}
                    </span>
                    <span className="text-xs opacity-60">{total}</span>
                    <IconChevron open={open} />
                  </button>
                  {open && (
                    <div className="pb-4 pl-12" data-testid={`book-content-section-body-${sec.id}`}>
                      {/* Direct-in-section pages */}
                      {sec.pages.map((p, pIdx) => (
                        <button key={p.id} onClick={() => openPage(p.id)}
                          className="w-full text-left py-3 border-t border-[#3b241a]/8 flex items-baseline gap-3 hover:opacity-90"
                          data-testid={`book-content-page-${p.id}`}>
                          <span className="text-[10px] opacity-50 w-6">
                            {String(pIdx + 1).padStart(2, "0")}
                          </span>
                          <span className="flex-1">
                            <span className="text-[16px]">{p.title}</span>
                            {p.subtitle && (
                              <span className="block text-[12px] opacity-60 mt-0.5">{p.subtitle}</span>
                            )}
                          </span>
                        </button>
                      ))}
                      {/* Subsections */}
                      {sec.subsections.map((sub) => {
                        const subOpen = openSubId === sub.id;
                        return (
                          <div key={sub.id} className="mt-2">
                            <button onClick={() => setOpenSubId(subOpen ? null : sub.id)}
                              className="w-full text-left py-3 flex items-center gap-3 opacity-90"
                              data-testid={`book-content-subsection-${sub.id}`}>
                              <IconChevron open={subOpen} />
                              <span className="font-serif text-[19px]">{sub.title}</span>
                            </button>
                            {subOpen && (
                              <div className="pl-6">
                                {sub.pages.map((p, pIdx) => (
                                  <button key={p.id} onClick={() => openPage(p.id)}
                                    className="w-full text-left py-2.5 flex items-baseline gap-3"
                                    data-testid={`book-content-page-${p.id}`}>
                                    <span className="text-[10px] opacity-50 w-6">
                                      {String(pIdx + 1).padStart(2, "0")}
                                    </span>
                                    <span className="flex-1 text-[15px]">{p.title}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
